// SSTables are real serialized bytes. Layout of one table (little-endian):
//
//   data section     repeated: u16 keyLen | key utf8 | u8 flags | f64 seq
//                              | u32 valLen | value utf8   (valLen=0 if tombstone)
//   sparse index     u32 count | repeated: u16 keyLen | key utf8 | u32 offset
//                    (one entry per `sparseEvery` keys, offset into data section)
//   bloom filter     u8 numHashes | u32 byteLen | bits
//   footer (12 B)    u32 indexOffset | u32 bloomOffset | u32 magic
//
// A get() binary-searches the sparse index, then scans forward through at
// most `sparseEvery` serialized entries. It never consults any in-memory map
// of the table's contents — the ArrayBuffer is the table.

import { BloomFilter } from "./bloom";
import { Entry, TableMeta, cmp } from "./types";

const MAGIC = 0x4c534d31; // "LSM1"
const FLAG_TOMBSTONE = 1;

const enc = new TextEncoder();
const dec = new TextDecoder();

export interface BuiltTable {
  buffer: ArrayBuffer;
  meta: Omit<TableMeta, "id" | "level">;
}

/** Serialize sorted entries into an SSTable buffer. */
export function buildTable(
  entries: Entry[],
  bloomBitsPerKey: number,
  sparseEvery: number,
): BuiltTable {
  if (entries.length === 0) throw new Error("buildTable: empty input");

  const keyBytes: Uint8Array[] = new Array(entries.length);
  const valBytes: (Uint8Array | null)[] = new Array(entries.length);
  let dataSize = 0;
  for (let i = 0; i < entries.length; i++) {
    const e = entries[i];
    keyBytes[i] = enc.encode(e.key);
    valBytes[i] = e.value === null ? null : enc.encode(e.value);
    dataSize += 2 + keyBytes[i].length + 1 + 8 + 4 + (valBytes[i]?.length ?? 0);
  }

  const bloom = BloomFilter.create(entries.length, bloomBitsPerKey);
  const sparse: { keyIdx: number; offset: number }[] = [];

  // Data section
  const data = new Uint8Array(dataSize);
  const dv = new DataView(data.buffer);
  let off = 0;
  let tombstones = 0;
  for (let i = 0; i < entries.length; i++) {
    const e = entries[i];
    bloom.add(e.key);
    if (i % sparseEvery === 0) sparse.push({ keyIdx: i, offset: off });
    dv.setUint16(off, keyBytes[i].length, true);
    off += 2;
    data.set(keyBytes[i], off);
    off += keyBytes[i].length;
    const tomb = e.value === null;
    if (tomb) tombstones++;
    dv.setUint8(off, tomb ? FLAG_TOMBSTONE : 0);
    off += 1;
    dv.setFloat64(off, e.seq, true);
    off += 8;
    const vb = valBytes[i];
    dv.setUint32(off, vb ? vb.length : 0, true);
    off += 4;
    if (vb) {
      data.set(vb, off);
      off += vb.length;
    }
  }

  // Sparse index section
  let indexSize = 4;
  for (const s of sparse) indexSize += 2 + keyBytes[s.keyIdx].length + 4;
  const index = new Uint8Array(indexSize);
  const idv = new DataView(index.buffer);
  idv.setUint32(0, sparse.length, true);
  let ioff = 4;
  for (const s of sparse) {
    const kb = keyBytes[s.keyIdx];
    idv.setUint16(ioff, kb.length, true);
    ioff += 2;
    index.set(kb, ioff);
    ioff += kb.length;
    idv.setUint32(ioff, s.offset, true);
    ioff += 4;
  }

  // Bloom section
  const bloomSize = 1 + 4 + bloom.bits.length;

  const total = dataSize + indexSize + bloomSize + 12;
  const out = new Uint8Array(total);
  const odv = new DataView(out.buffer);
  out.set(data, 0);
  out.set(index, dataSize);
  let boff = dataSize + indexSize;
  odv.setUint8(boff, bloom.numHashes);
  odv.setUint32(boff + 1, bloom.bits.length, true);
  out.set(bloom.bits, boff + 5);
  odv.setUint32(total - 12, dataSize, true);
  odv.setUint32(total - 8, dataSize + indexSize, true);
  odv.setUint32(total - 4, MAGIC, true);

  return {
    buffer: out.buffer,
    meta: {
      minKey: entries[0].key,
      maxKey: entries[entries.length - 1].key,
      size: total,
      entryCount: entries.length,
      tombstoneCount: tombstones,
    },
  };
}

export interface ProbeResult {
  entry: Entry | undefined;
  /** False when the bloom filter proved the key absent (no data scan ran). */
  bloomPassed: boolean;
  /** Serialized entries decoded while scanning from the sparse-index seek. */
  entriesScanned: number;
  /** Sparse-index slot the binary search landed on (-1 if before the first). */
  sparseSlot: number;
  /** Byte offset in the data section where the forward scan began. */
  seekOffset: number;
  /** The indexed key at that slot — the anchor the scan started from. */
  seekAnchor: string;
  /** Comparisons performed by the sparse-index binary search. */
  indexComparisons: number;
}

/** Reads an SSTable directly from its serialized bytes. */
export class SSTableReader {
  private readonly dv: DataView;
  private readonly bytes: Uint8Array;
  private readonly indexOffset: number;
  private readonly bloom: BloomFilter;
  private readonly sparseKeys: string[] = [];
  private readonly sparseOffsets: number[] = [];

  constructor(buffer: ArrayBuffer) {
    this.bytes = new Uint8Array(buffer);
    this.dv = new DataView(buffer);
    const n = buffer.byteLength;
    if (this.dv.getUint32(n - 4, true) !== MAGIC) {
      throw new Error("corrupt SSTable: bad magic");
    }
    this.indexOffset = this.dv.getUint32(n - 12, true);
    const bloomOffset = this.dv.getUint32(n - 8, true);

    // Parse sparse index (small: one entry per sparseEvery keys).
    let off = this.indexOffset;
    const count = this.dv.getUint32(off, true);
    off += 4;
    for (let i = 0; i < count; i++) {
      const klen = this.dv.getUint16(off, true);
      off += 2;
      this.sparseKeys.push(dec.decode(this.bytes.subarray(off, off + klen)));
      off += klen;
      this.sparseOffsets.push(this.dv.getUint32(off, true));
      off += 4;
    }

    const numHashes = this.dv.getUint8(bloomOffset);
    const blen = this.dv.getUint32(bloomOffset + 1, true);
    this.bloom = new BloomFilter(
      this.bytes.slice(bloomOffset + 5, bloomOffset + 5 + blen),
      numHashes,
    );
  }

  get bloomBits(): number {
    return this.bloom.bits.length * 8;
  }
  get bloomHashes(): number {
    return this.bloom.numHashes;
  }
  get sparseIndexSize(): number {
    return this.sparseKeys.length;
  }
  /** First `maxBytes` of the filter's bit vector (for inspection UIs). */
  bloomPreview(maxBytes = 32): Uint8Array {
    return this.bloom.bits.subarray(0, maxBytes);
  }

  /**
   * Point lookup against the serialized bytes.
   *
   * Three stages, cheapest first:
   *   1. Bloom filter — k bit tests. A negative is definitive, so the data
   *      section is never touched for most absent keys.
   *   2. Sparse index binary search — O(log(n/sparseEvery)) comparisons to
   *      find the greatest indexed key <= target, giving a byte offset.
   *   3. Forward scan — decode entries from that offset until the key is
   *      found or passed. Bounded by sparseEvery (16) entries.
   */
  get(key: string): ProbeResult {
    if (!this.bloom.mayContain(key)) {
      return {
        entry: undefined,
        bloomPassed: false,
        entriesScanned: 0,
        sparseSlot: -1,
        seekOffset: -1,
        seekAnchor: "",
        indexComparisons: 0,
      };
    }
    // Greatest sparse entry with key <= target.
    let lo = 0;
    let hi = this.sparseKeys.length - 1;
    let start = -1;
    let indexComparisons = 0;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      indexComparisons++;
      if (cmp(this.sparseKeys[mid], key) <= 0) {
        start = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    if (start < 0) {
      // Key sorts before the first entry in the table: bloom false positive.
      return {
        entry: undefined,
        bloomPassed: true,
        entriesScanned: 0,
        sparseSlot: -1,
        seekOffset: -1,
        seekAnchor: "",
        indexComparisons,
      };
    }
    const seekOffset = this.sparseOffsets[start];
    const seekAnchor = this.sparseKeys[start];
    let off = seekOffset;
    let scanned = 0;
    while (off < this.indexOffset) {
      const { entry, next } = this.decodeAt(off);
      scanned++;
      const c = cmp(entry.key, key);
      if (c === 0) {
        return {
          entry,
          bloomPassed: true,
          entriesScanned: scanned,
          sparseSlot: start,
          seekOffset,
          seekAnchor,
          indexComparisons,
        };
      }
      if (c > 0) break;
      off = next;
    }
    return {
      entry: undefined,
      bloomPassed: true,
      entriesScanned: scanned,
      sparseSlot: start,
      seekOffset,
      seekAnchor,
      indexComparisons,
    };
  }

  /** Full in-order scan — used by compaction and range queries. */
  *iterate(): IterableIterator<Entry> {
    let off = 0;
    while (off < this.indexOffset) {
      const { entry, next } = this.decodeAt(off);
      yield entry;
      off = next;
    }
  }

  /** Entries with key in [lo, hi), seeking via the sparse index. */
  *range(lo: string, hi: string): IterableIterator<Entry> {
    let start = 0;
    for (let i = this.sparseKeys.length - 1; i >= 0; i--) {
      if (cmp(this.sparseKeys[i], lo) <= 0) {
        start = this.sparseOffsets[i];
        break;
      }
    }
    let off = start;
    while (off < this.indexOffset) {
      const { entry, next } = this.decodeAt(off);
      off = next;
      if (cmp(entry.key, lo) < 0) continue;
      if (cmp(entry.key, hi) >= 0) return;
      yield entry;
    }
  }

  private decodeAt(off: number): { entry: Entry; next: number } {
    const klen = this.dv.getUint16(off, true);
    off += 2;
    const key = dec.decode(this.bytes.subarray(off, off + klen));
    off += klen;
    const flags = this.dv.getUint8(off);
    off += 1;
    const seq = this.dv.getFloat64(off, true);
    off += 8;
    const vlen = this.dv.getUint32(off, true);
    off += 4;
    let value: string | null = null;
    if ((flags & FLAG_TOMBSTONE) === 0) {
      value = dec.decode(this.bytes.subarray(off, off + vlen));
    }
    off += vlen;
    return { entry: { key, value, seq }, next: off };
  }
}
